/**
 * @fileoverview Componente Molécula para mostrar texto truncado con opción de expandir/colapsar ('Leer más') en respuestas rápidas.
 * Cumple con Atomic Design (Molecule) y optimizaciones de renderizado (React.memo, useCallback, useMemo).
 *
 * @module ReadMoreMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 3.1.0
 */

import React, {
  useState,
  memo,
  useMemo,
  useCallback
} from "react";
import { useTranslation } from 'react-i18next';

/**
 * @typedef {Object} ReadMoreMCProps
 * @description Props del componente ReadMoreMC.
 * @property {string|React.ReactNode} children - Texto o nodo a mostrar con truncamiento.
 */

/**
 * Componente Molécula para mostrar texto truncado con opción de expandir/colapsar ('Leer más').
 *
 * @component ReadMoreMC
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Trunca el texto a un límite de caracteres y permite expandir/colapsar.
 * - Soporta cualquier nodo o string como children.
 * - Optimizado con React.memo, useCallback y useMemo.
 *
 * **Atomic Design:**
 * - **Nivel:** Molecule
 * - **Composición:** No depende de otros componentes complejos.
 *
 * @param {ReadMoreMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Texto truncado con opción de expandir/colapsar
 *
 * @example
 * <ReadMoreMC>Texto largo que será truncado...</ReadMoreMC>
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */

const ReadMoreMC = memo(({ children }) => {
  const { t } = useTranslation('FastAnswer');
  const [isReadMore, setIsReadMore] = useState(true);

  /**
   * Límite de caracteres para truncamiento.
   * @type {number}
   */
  const CHARACTER_LIMIT = 70;

  /**
   * Texto a mostrar, convertido a string si es necesario.
   * @type {string}
   */
  const text = useMemo(() => {
    return typeof children === 'string' ? children : String(children || '');
  }, [children]);

  /**
   * Indica si el texto necesita truncamiento.
   * @type {boolean}
   */
  const needsTruncation = useMemo(() => {
    return text.length > CHARACTER_LIMIT;
  }, [text.length, CHARACTER_LIMIT]);

  /**
   * Texto truncado si es necesario.
   * @type {string}
   */
  const truncatedText = useMemo(() => {
    return needsTruncation ? text.slice(0, CHARACTER_LIMIT) : text;
  }, [text, needsTruncation, CHARACTER_LIMIT]);

  /**
   * Texto a mostrar según el estado expandido/colapsado.
   * @type {string}
   */
  const displayText = useMemo(() => {
    if (!needsTruncation) return text;
    return isReadMore ? truncatedText : text;
  }, [needsTruncation, isReadMore, truncatedText, text]);

  /**
   * Alterna el estado de expandido/colapsado.
   * @function toggleReadMore
   * @memberof ReadMoreMC
   */
  const toggleReadMore = useCallback(() => {
    setIsReadMore(prev => !prev);
  }, []);

  /**
   * Texto del botón de acción ('Leer más' / 'Mostrar menos').
   * @type {string}
   */
  const buttonText = useMemo(() => {
    return isReadMore ? t('readMore') : t('showLess');
  }, [isReadMore, t]);

  // Si no necesita truncamiento, renderizar solo el texto
  if (!needsTruncation) {
    return (
      <small className="tw-whitespace-pre-line tw-text-sm tw-text-gray-700 tw-block">
        {text}
      </small>
    );
  }

  return (
    <small className="tw-whitespace-pre-line tw-text-sm tw-text-gray-700 tw-block">
      {displayText}
      <span
        onClick={toggleReadMore}
        className="tw-text-blue-600 tw-cursor-pointer hover:tw-text-blue-800 tw-transition-colors tw-ml-1"
        role="button"
        tabIndex={0}
        aria-label={buttonText}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleReadMore();
          }
        }}
      >
        {buttonText}
      </span>
    </small>
  );
});

ReadMoreMC.displayName = 'ReadMoreMC';

export default ReadMoreMC;
